import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { Order } from '@klareospace/pharos-lib-types';
import {
  EntityIcon,
  InlineLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextCell,
} from '@klareospace/pharos-lib-web-utils';

import { useCarriersForSelect } from '&/features/settings/carriers/hooks';

import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { ORDER_FIELD_EDIT_STATE } from '../hooks/use-order-editability';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CarrierInlineEditorProps {
  readonly fieldState: OrderFieldEditState;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Requested carrier — inline Select + confirm/cancel, mirroring PHA-270's
 * TaskCarrierDriverSection pattern. Rejection reverts to the previous value.
 */
export function CarrierInlineEditor({ fieldState, lockReason, order }: CarrierInlineEditorProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const carrierOptions = useCarriersForSelect({
    enabled: fieldState === ORDER_FIELD_EDIT_STATE.EDITABLE,
    enabledOnSiteId: order.siteId,
    valueKey: 'code',
  });

  const { draft, handleCancel, handleConfirm, isEditing, isPending, isValid, setDraft, startEdit } =
    useInlineOrderFieldEdit({
      buildPatch: (requestedCarrierCode: string) => ({ requestedCarrierCode }),
      isValid: (value: string): boolean => value !== '',
      order,
      successKey: 'drawer.edit.carrier.success',
      value: order.requestedCarrier?.code ?? '',
    });

  const carrierName = order.requestedCarrier?.name;

  if (isEditing) {
    return (
      <div className="mt-1 flex items-center gap-1">
        <Select disabled={isPending} onValueChange={setDraft} value={draft}>
          <SelectTrigger className="h-7 flex-1 text-sm">
            <SelectValue placeholder={t('drawer.edit.carrier.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {carrierOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InlineEditActions isPending={isPending} isValid={isValid} onCancel={handleCancel} onConfirm={handleConfirm} />
      </div>
    );
  }

  const content = carrierName ? (
    <InlineLabel labelClassName="text-foreground" prefix={<EntityIcon alt={carrierName} size="xs" />} size="sm">
      {carrierName}
    </InlineLabel>
  ) : (
    <TextCell>—</TextCell>
  );

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      {content}
    </EditableFieldWrapper>
  );
}
